"use client";

import type { ReactNode } from "react";
import {
	CONTENT_TOOLTIP_SHORT,
	ContentTooltip,
} from "../design-system/tooltip";
import { CheckIcon } from "lucide-react";
import type { FieldBooleanChrome } from "../../lib/field-types/contracts";
import { cn } from "../../lib";
import { Checkbox } from "../checkbox";
import { ToggleSwitch } from "../custom/toggle-switch";
import { Switch } from "../switch";
import {
	CHECKBOX_BORDERED_ROW_CLASS,
	CHECKBOX_BUTTON_CLASS,
	CHECKBOX_PLAIN_CLASS,
} from "./boolean-checkbox-chrome";
import { fieldErrorId, fieldMixedId } from "./field-a11y";
import { FieldShell, type FieldLayoutHint } from "./field-shell";

export type BooleanFieldPresentation =
	| "segmented"
	| "switch"
	| "tiles"
	| "checkbox";

export interface BooleanFieldProps {
	id: string;
	/** String or rich label (checkbox chrome). */
	label: ReactNode;
	value: boolean | null;
	onChange: (value: boolean) => void;
	description?: string;
	labelInfo?: ReactNode;
	labelInfoAriaLabel?: string;
	error?: ReactNode;
	reserveErrorSpace?: boolean;
	layoutHint?: FieldLayoutHint;
	presentation?: BooleanFieldPresentation;
	/** Checkbox row chrome; default `plain`. Ignored for non-checkbox presentations. */
	chrome?: FieldBooleanChrome;
	/** Button chrome vertical align. */
	align?: "start" | "center";
	mixed?: boolean;
	mixedTooltip?: ReactNode;
	mixedPlaceholder?: string;
	trueLabel?: string;
	falseLabel?: string;
	className?: string;
	disabled?: boolean;
	disabledReason?: string;
	testId?: string;
	name?: string;
	onBlur?: () => void;
}

function checkboxChromeClass(
	chrome: FieldBooleanChrome,
	align: "start" | "center",
): string {
	if (chrome === "button") {
		return cn(CHECKBOX_BUTTON_CLASS, align === "start" && "items-start");
	}
	if (chrome === "bordered_row") {
		return CHECKBOX_BORDERED_ROW_CLASS;
	}
	return CHECKBOX_PLAIN_CLASS;
}

export function BooleanField({
	id,
	label,
	value,
	onChange,
	description,
	labelInfo,
	labelInfoAriaLabel,
	error,
	reserveErrorSpace = false,
	layoutHint,
	presentation = "segmented",
	chrome = "plain",
	align = "center",
	mixed = false,
	mixedTooltip,
	mixedPlaceholder = "Mixed values",
	trueLabel = "Yes",
	falseLabel = "No",
	className,
	disabled = false,
	disabledReason,
	testId,
	name,
	onBlur,
}: BooleanFieldProps) {
	const mixedId = fieldMixedId(id);
	const showError = Boolean(error);
	const errorId =
		showError || reserveErrorSpace ? fieldErrorId(id) : undefined;
	const isMixed = mixed || value === null;
	const isInteractionDisabled = disabled || Boolean(disabledReason);
	const labelText = typeof label === "string" ? label : undefined;

	function wrapControl(control: ReactNode) {
		if (disabledReason && !(isMixed && mixedTooltip)) {
			return (
								<ContentTooltip
					{...CONTENT_TOOLTIP_SHORT}
					side="left"
					contentClassName="max-w-xs whitespace-pre-line"
					content={disabledReason}
				>
					<span className="inline-flex shrink-0">{control}</span>
				</ContentTooltip>
			);
		}
		return control;
	}

	function wrapMixedRow(row: ReactNode) {
		if (!(isMixed && mixedTooltip)) {
			return row;
		}
		return (
						<ContentTooltip
				asChild
				{...CONTENT_TOOLTIP_SHORT}
				side="left"
				contentClassName="max-w-sm p-2"
				content={mixedTooltip}
			>
				<div className="w-full min-w-0">{row}</div>
			</ContentTooltip>
		);
	}

	if (presentation === "checkbox") {
		const checkboxControl = (
			<>
				{name ? (
					<input
						type="hidden"
						name={name}
						value={isMixed ? "" : value === true ? "true" : "false"}
						readOnly
					/>
				) : null}
				<Checkbox
					id={id}
					name={name}
					checked={isMixed ? "indeterminate" : value === true}
					disabled={isInteractionDisabled}
					onCheckedChange={(checked) => onChange(checked === true)}
					onBlur={onBlur}
					className={cn(
						"shrink-0",
						chrome === "button" && align === "start" && "mt-px",
					)}
					aria-label={labelText}
					aria-invalid={Boolean(error) ? true : undefined}
					aria-describedby={errorId}
				/>
			</>
		);

		const labelRow = (
			<label
				htmlFor={id}
				data-testid={testId}
				data-boolean-chrome={chrome}
				className={cn(
					checkboxChromeClass(chrome, align),
					isInteractionDisabled && "cursor-not-allowed opacity-60",
					className,
				)}
			>
				{wrapControl(checkboxControl)}
				<span
					className={cn(
						"min-w-0 flex-1",
						chrome === "button"
							? "font-normal leading-snug"
							: "truncate text-sm font-medium leading-none",
					)}
					title={labelText}
				>
					{label}
				</span>
				{description ? (
					<span className="sr-only">{description}</span>
				) : null}
				{isMixed ? (
					<span id={mixedId} className="sr-only">
						{mixedPlaceholder}
					</span>
				) : null}
			</label>
		);

		return (
			<div className="w-full">
				{wrapMixedRow(labelRow)}
				{errorId ? (
					<p
						id={errorId}
						role={showError ? "alert" : undefined}
						aria-hidden={!showError}
						className="mt-1.5 min-h-5 text-xs text-destructive"
					>
						{showError ? error : null}
					</p>
				) : null}
			</div>
		);
	}

	if (presentation === "switch") {
		return (
			<FieldShell
				id={id}
				label={label}
				description={description}
				labelInfo={labelInfo}
				labelInfoAriaLabel={labelInfoAriaLabel}
				error={error}
				reserveErrorSpace={reserveErrorSpace}
				layoutHint={layoutHint}
				className={className}
				describedById={isMixed ? mixedId : undefined}
			>
				{isMixed ? (
					<p id={mixedId} className="mb-2 text-xs text-muted-foreground">
						{mixedTooltip ?? mixedPlaceholder}
					</p>
				) : null}
				<div className="flex items-center gap-2">
					{wrapControl(
						<Switch
							id={id}
							name={name}
							checked={value === true}
							disabled={disabled}
							onCheckedChange={(checked) => onChange(checked)}
							onBlur={onBlur}
							aria-label={labelText ?? id}
							aria-invalid={Boolean(error) ? true : undefined}
						/>,
					)}
					<span className="text-sm text-muted-foreground">
						{value === true ? trueLabel : falseLabel}
					</span>
				</div>
			</FieldShell>
		);
	}

	if (presentation === "tiles") {
		return (
			<FieldShell
				id={id}
				label={label}
				description={description}
				labelInfo={labelInfo}
				labelInfoAriaLabel={labelInfoAriaLabel}
				error={error}
				reserveErrorSpace={reserveErrorSpace}
				layoutHint={layoutHint}
				className={className}
				describedById={isMixed ? mixedId : undefined}
			>
				{isMixed ? (
					<p id={mixedId} className="mb-2 text-xs text-muted-foreground">
						{mixedTooltip ?? mixedPlaceholder}
					</p>
				) : null}
				<div
					role="radiogroup"
					aria-label={labelText ?? id}
					aria-invalid={Boolean(error) ? true : undefined}
					onBlur={onBlur}
					className="grid grid-cols-2 gap-2"
				>
					{(
						[
							{ value: false, label: falseLabel },
							{ value: true, label: trueLabel },
						] as const
					).map((option) => {
						const selected = !isMixed && value === option.value;
						return (
							<button
								key={String(option.value)}
								type="button"
								name={name}
								role="radio"
								aria-checked={selected}
								disabled={disabled}
								onClick={() => onChange(option.value)}
								className={cn(
									"relative rounded-md border px-3 py-2 text-left text-sm transition-colors",
									selected
										? "border-primary bg-primary/5"
										: "border-border hover:bg-muted/40",
								)}
							>
								{selected ? (
									<CheckIcon
										className="absolute top-2 right-2 size-3.5 text-primary"
										aria-hidden
									/>
								) : null}
								{option.label}
							</button>
						);
					})}
				</div>
			</FieldShell>
		);
	}

	const segmentedControl = (
		<ToggleSwitch
			layoutId={`${id}-boolean`}
			value={value === true ? "true" : value === false ? "false" : "true"}
			mixed={isMixed}
			disabled={isInteractionDisabled}
			optionDisplay="text"
			highlightWhenRightActive
			aria-invalid={Boolean(error) ? true : undefined}
			onValueChange={(next) => onChange(next === "true")}
			options={[
				{ id: "false", ariaLabel: falseLabel, label: falseLabel },
				{ id: "true", ariaLabel: trueLabel, label: trueLabel },
			]}
		/>
	);

	return (
		<div
			data-testid={testId}
			className={cn(
				"flex w-full items-center justify-between gap-4",
				className,
			)}
			onBlur={onBlur}
		>
			{name ? <input type="hidden" name={name} value={String(value === true)} /> : null}
			<FieldShell
				id={id}
				label={label}
				description={description}
				labelInfo={labelInfo}
				labelInfoAriaLabel={labelInfoAriaLabel}
				error={error}
				reserveErrorSpace={reserveErrorSpace}
				layoutHint={layoutHint}
				className="min-w-0 flex-1"
				describedById={isMixed ? mixedId : undefined}
			>
				{isMixed && !mixedTooltip ? (
					<p id={mixedId} className="mb-2 text-xs text-muted-foreground">
						{mixedPlaceholder}
					</p>
				) : null}
			</FieldShell>
			{wrapControl(
				isMixed && mixedTooltip ? (
										<ContentTooltip
						{...CONTENT_TOOLTIP_SHORT}
						side="left"
						contentClassName="max-w-sm p-2"
						content={mixedTooltip}
					>
						<span className="inline-flex shrink-0">
															{segmentedControl}
														</span>
					</ContentTooltip>
				) : (
					segmentedControl
				),
			)}
		</div>
	);
}
